"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Badge, Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { shopMarketplaceApi } from "../api/client";

const STATUS_LABELS: Record<string, string> = {
  awaiting_payment: "ລໍຖ້າຊຳລະ",
  paid: "ຊຳລະແລ້ວ",
  ready_pickup: "ພ້ອມຮັບ",
  confirmed: "ຢືນຢັນ",
  completed: "ສຳເລັດ",
  canceled: "ຍົກເລີກ",
};

export function ShopMarketplaceOrdersPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["shop-marketplace-orders", shopId],
    queryFn: () => shopMarketplaceApi.listOrders(shopId!),
    enabled: !!shopId,
  });

  const updateStatus = useMutation({
    mutationFn: ({
      orderId,
      status,
    }: {
      orderId: string;
      status: "confirmed" | "ready_pickup" | "completed" | "canceled";
    }) => shopMarketplaceApi.updateOrderStatus(shopId!, orderId, status),
    onSuccess: () => {
      toast.success("ອັບເດດສະຖານະແລ້ວ");
      qc.invalidateQueries({ queryKey: ["shop-marketplace-orders", shopId] });
      qc.invalidateQueries({ queryKey: ["shop-marketplace-pending-orders", shopId] });
    },
    onError: () => toast.error("ອັບເດດບໍ່ສຳເລັດ"),
  });

  if (!shopId) return null;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.marketplace.read}>
      <Header />
      <Main>
        <Breadcrumb items={shopBreadcrumbs.marketplaceOrders(shopId)} />
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold">ຄຳສັ່ງຊື້ Marketplace</h1>
          <Button
            variant="outline"
            onClick={() =>
              nav({
                to: "/app/shops/$shopId/marketplace",
                params: { shopId: shopId! },
              })
            }
          >
            ກັບໄປ listings
          </Button>
        </div>

        {isLoading ? (
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        ) : !data?.data.length ? (
          <p className="text-muted-foreground">ຍັງບໍ່ມີຄຳສັ່ງຊື້</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {data.data.map((order) => (
              <li
                key={order.id}
                className="flex flex-wrap items-center justify-between gap-3 p-4"
              >
                <div>
                  <p className="font-medium">
                    #{order.id.slice(0, 8)} · {order.itemCount} ລາຍການ
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(order.createdAt).toLocaleString("lo-LA")}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">
                    {STATUS_LABELS[order.status] ?? order.status}
                  </Badge>
                  <span className="font-semibold">
                    {order.total.toLocaleString("lo-LA")} {order.currency}
                  </span>
                  {order.status === "ready_pickup" ? (
                    <Button
                      size="sm"
                      onClick={() =>
                        updateStatus.mutate({
                          orderId: order.id,
                          status: "completed",
                        })
                      }
                    >
                      ຮັບແລ້ວ
                    </Button>
                  ) : null}
                  {order.status === "paid" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        updateStatus.mutate({
                          orderId: order.id,
                          status: "confirmed",
                        })
                      }
                    >
                      ຢືນຢັນ
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Main>
    </RequireMerchantPermission>
  );
}
